/**
 * @file PolygonsLayer.tsx
 * @description
 * Pure presentational layer for rendering congressional district polygons
 * as SVG `<path>` elements.
 *
 * This component:
 * - Expects `polys` as a valid GeoJSON FeatureCollection of polygons.
 * - Expects a preconfigured D3 `path` generator (already wired with a projection).
 * - Uses `fillForId` to decide the choropleth color for a given district feature.
 * - Uses `useVoteColor` hook to get vote type colors for hover shadow effects.
 * - Emits hover and click events so parent components can drive shared tooltip
 *   and click-to-lock selection behavior.
 *
 * IMPORTANT ASSUMPTION:
 * Each polygon feature's `feature.id` is already in the same ID format used
 * by the bill voting data, e.g. `"118-ca-29"` (congress-state-district format).
 * We are *not* doing any string normalization or reformatting here.
 *
 * Props:
 * - onHover(id, evt): called on mouse enter / move.
 * - onLeave(): called on mouse leave.
 * - onClick(id, evt): called on path click for selection.
 *
 * @see useVoteColor - Hook used to get vote type colors for hover shadows
 */

import type { FeatureCollection } from 'geojson';
import * as React from 'react';

import { useVoteColor } from '@/hooks/useVoteColor';

interface PolygonsLayerProps {
  polys: FeatureCollection | null;
  path: (feature: d3.GeoPermissibleObjects) => string | null;
  fillForId: (id: string) => string;
  hoveredIds?: string[];
  dimInactive?: boolean;

  onHover?: (id: string, e: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
  onLeave?: () => void;
  onClick?: (id: string, e: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
}

interface PolygonFeatureProperties {
  district_id?: string;
  GEOID?: string;
  geoid?: string;
}

export function PolygonsLayer({
  polys,
  path,
  fillForId,
  hoveredIds,
  dimInactive,
  onHover,
  onLeave,
  onClick,
}: PolygonsLayerProps) {
  // Get vote color function for hover shadow effects
  // Must be called before early return to satisfy React Hooks rules
  const getVoteColorForId = useVoteColor();

  if (!polys) return null;

  // Collect unique vote colors for hovered districts to create filters
  const hoveredColors = new Map<string, string>();
  polys.features.forEach((f, i) => {
    const properties = (f.properties as PolygonFeatureProperties | null) ?? {};
    const fallbackId = properties.district_id ?? properties.GEOID ?? properties.geoid ?? String(i);
    const id = (f.id as string) ?? fallbackId;

    if (hoveredIds?.includes(id)) {
      const voteColor = getVoteColorForId(id);
      // Create a safe filter ID from the color (replace # and special chars)
      const filterId = `hover-glow-${voteColor.replace(/[^a-zA-Z0-9]/g, '-')}`;
      hoveredColors.set(filterId, voteColor);
    }
  });

  return (
    <g>
      {/* Define filters for each unique hovered color */}
      {hoveredColors.size > 0 && (
        <defs>
          {Array.from(hoveredColors.entries()).map(([filterId, color]) => (
            <filter key={filterId} id={filterId} x="-50%" y="-50%" width="200%" height="200%">
              {/* Multiple shadow layers with decreasing opacity are applied to make the shadow color more prominent.
                  Layering multiple instances creates a more saturated, intense glow effect. */}
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={color} floodOpacity="1.0" />
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={color} floodOpacity="0.8" />
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={color} floodOpacity="0.6" />
            </filter>
          ))}
        </defs>
      )}
      {polys.features.map((f, i) => {
        // We assume `f.id` is already something like "118-ca-29" (congress-state-district format).
        // We include a tiny fallback to properties.* so the UI doesn't explode
        // if one odd feature is missing `id`. We do NOT reshape formats.
        const properties = (f.properties as PolygonFeatureProperties | null) ?? {};
        const fallbackId =
          properties.district_id ?? properties.GEOID ?? properties.geoid ?? String(i);

        const id = (f.id as string) ?? fallbackId;
        const pathString = path(f) ?? '';

        const isHovered = hoveredIds?.includes(id) ?? false;
        const shouldDim = dimInactive && !isHovered;
        const fillColor = fillForId(id);
        const voteColor = getVoteColorForId(id);

        // Create filter ID for this district's vote color if hovered
        const filterId = isHovered
          ? `hover-glow-${voteColor.replace(/[^a-zA-Z0-9]/g, '-')}`
          : undefined;

        return (
          <path
            key={id}
            d={pathString}
            fill={fillColor}
            stroke="#000000"
            strokeWidth={0.3}
            opacity={shouldDim ? 0.5 : 1}
            filter={filterId ? `url(#${filterId})` : undefined}
            shapeRendering="geometricPrecision"
            onMouseEnter={(e) => onHover?.(id, e)}
            onMouseMove={(e) => onHover?.(id, e)}
            onMouseLeave={onLeave}
            onClick={(e) => onClick?.(id, e)}
          />
        );
      })}
    </g>
  );
}
